import React from 'react';
import { useAudio } from './hooks/useAudio';

interface GameIntroProps {
  onComplete: () => void;
}

/**
 * Game Intro Component
 * Plays kbc_4_ringtone.mp3 (first 6 seconds) with dramatic animation
 * Shown only before the first question
 */
const GameIntro: React.FC<GameIntroProps> = ({ onComplete }) => {
  const { audioMuted } = useAudio();

  // Use useLayoutEffect for immediate execution on mount (runs before paint)
  React.useLayoutEffect(() => {
    // Play intro ringtone (first 6 seconds only)
    let audio: HTMLAudioElement | null = null;

    if (!audioMuted) {
      audio = new Audio('/audio/kbc_4_ringtone.mp3');
      // FAILSAFE: Set volume based on audio state
      audio.volume = audioMuted ? 0 : 0.420;
      
      audio.play().catch(() => {
        console.debug('Intro ringtone audio not available');
      });

      // Stop audio after 6 seconds
      setTimeout(() => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      }, 6000);
    }

    // Call onComplete after 6 seconds
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 6000);

    // Cleanup on unmount
    return () => {
      clearTimeout(completeTimer);
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [audioMuted, onComplete]); // Dependencies: audioMuted and onComplete

  return (
    <div className="min-h-screen bg-gradient-to-br from-kbc-dark via-kbc-dark-accent to-kbc-dark flex items-center justify-center relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div 
          className="absolute top-1/2 left-1/2 w-96 h-96 bg-kbc-gold/20 rounded-full blur-3xl animate-pulse-slow"
          style={{
            transform: 'translate(-50%, -50%)',
            animation: 'pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          }}
        />
        <div 
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-maersk-blue/20 rounded-full blur-2xl"
          style={{
            animation: 'spin 8s linear infinite',
          }}
        />
      </div>

      {/* Center content */}
      <div className="relative z-10 text-center animate-scale-in">
        <div className="mb-8 animate-bounce-slow">
          <div className="w-36 h-36 mx-auto rounded-full flex items-center justify-center mb-6"
               style={{
                 animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
               }}>
            <img 
              src="/kaunbanegashipmentcocaptain.svg" 
              alt="KBC Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 animate-fade-in"
              style={{
                textShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
              }}>
            KBC
          </h1>
          <h2 className="text-3xl md:text-4xl font-semibold text-kbc-gold animate-fade-in"
              style={{
                animationDelay: '0.3s',
                textShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
              }}>
            Kaun Banega CoCaptain?
          </h2>
        </div>

        {/* Loading indicator */}
        <div className="mt-12 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="flex justify-center items-center gap-2">
            <div className="w-3 h-3 bg-kbc-gold rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-3 h-3 bg-kbc-gold rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-3 h-3 bg-kbc-gold rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
          <p className="text-white/80 mt-4 text-lg">Get Ready...</p>
        </div>
      </div>

      {/* Spotlight effect */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at center, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
          animation: 'fadeIn 2s ease-out forwards',
        }}
      />
    </div>
  );
};

export default GameIntro;